import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountModel, QuotePageData } from '@cpak/adp-cpak-bff-service-models';
import { RelationTypeEnum, RelationModel } from '@cpak/adp-cpak-quotes-service-models';
import { ACCOUNT_UNASSIGNED_ID, newPackId } from '@cpak/consts';
import { DialogOutletService } from './dialog-outlet';
import { RoutingUrls } from '@cpak/enums';
import { OpenPropertiesModal } from '@cpak/interfaces';

@Injectable({
  providedIn: 'root',
})
export class PakPropertiesService extends DialogOutletService {
  private createFromExisting$ = new BehaviorSubject<boolean>(false);
  private displaySettings$ = new BehaviorSubject<boolean>(true);
  private isMakeContract$ = new BehaviorSubject<boolean>(false);
  private isMakePrototype$ = new BehaviorSubject<boolean>(false);
  private creationMode$ = new BehaviorSubject<RelationTypeEnum | string | null>(null);
  private countryId$ = new BehaviorSubject<string | null>(null);
  private territoriesIds$ = new BehaviorSubject<string[] | null>(null);
  private account$ = new BehaviorSubject<AccountModel | null>(null);
  private quoteData$ = new BehaviorSubject<QuotePageData | null>(null);
  private updatedQuoteData$ = new BehaviorSubject<QuotePageData | null>(null);
  private currencyLabel$ = new BehaviorSubject<string | null>(null);
  private parentRelations$ = new BehaviorSubject<RelationModel[] | null>(null);
  private quoteId = '';
  private navigateToPak = false;
  private accountIdToNavigate: string | null = null;
  constructor(router: Router) {
    super(router, 'properties');
  }

  public override onCloseModal(id = '', navigateToPak = false, accountIdToNavigate: string | null = null): void {
    this.quoteId = id;
    this.navigateToPak = navigateToPak;
    this.accountIdToNavigate = accountIdToNavigate;
    super.onCloseModal();
  }

  public onOpenPropertiesModal(config: OpenPropertiesModal): void {
    this.quoteId = '';
    this.displaySettings$.next(config.displaySettings ?? true);
    this.isMakeContract$.next(config.isMakeContract || false);
    this.isMakePrototype$.next(config.isMakePrototype || false);
    this.creationMode$.next(config.creationType || null);
    this.countryId$.next(config.countryId || null);
    this.account$.next(config.account || null);
    this.currencyLabel$.next(config.currencyLabel || null);
    this.parentRelations$.next(config.parentRelations || null);
    this.setQuoteData(config.quoteData || null);
    super.onOpenModal(config.id);
  }

  public onOpenCreateFromExistingModal(config: OpenPropertiesModal): void {
    this.createFromExisting$.next(true);
    this.account$.next(config.account || null);
    this.countryId$.next(config.countryId || null);
    this.territoriesIds$.next(config.territoriesIds || null);
    super.onOpenModal(config.id);
  }

  public navigateToPrototype(prototypeId: string): void {
    this.router.navigate([RoutingUrls.Prototypes, prototypeId]);
  }

  public override outletNavigation(
    activatedRoute: ActivatedRoute,
    outletStatus: string | null,
    featureUrl = './'
  ): void {
    const isAccountPage = this.router.url.includes(RoutingUrls.Accounts);
    const shouldRedirectToQuote = !!(this.navigateToPak && this.quoteId);
    const shouldRedirectToAccount = !!this.accountIdToNavigate && isAccountPage;
    const url = shouldRedirectToQuote
      ? `/${RoutingUrls.Quotes}/${this.quoteId}`
      : shouldRedirectToAccount
      ? `/${RoutingUrls.Accounts}/${this.accountIdToNavigate}`
      : featureUrl;
    // don't show outlet in url for new pack creation
    const skipLocationChange =
      outletStatus === newPackId ||
      (!!outletStatus && (!!this.getCreationMode() || this.getIsMakeContract() || this.getIsMakePrototype()));

    const state =
      !shouldRedirectToQuote && shouldRedirectToAccount ? { accountIdToSelect: this.accountIdToNavigate! } : null;
    super.outletNavigation(activatedRoute, outletStatus, url, skipLocationChange, state);
  }

  public getDisplaySettings(): boolean {
    return this.displaySettings$.getValue();
  }

  public getCreationMode(): RelationTypeEnum | string | null {
    return this.creationMode$.getValue();
  }

  public getCountryId(): string | null {
    return this.countryId$.getValue();
  }

  public getTerritoriesIds(): string[] | null {
    return this.territoriesIds$.getValue();
  }

  public getAccountDetail(): AccountModel | null {
    const account = this.account$.getValue();
    return account?.id !== ACCOUNT_UNASSIGNED_ID ? account : null;
  }

  public getQuoteData(): Observable<QuotePageData | null> {
    return this.quoteData$.asObservable();
  }

  public getUpdatedQuoteData(): Observable<QuotePageData | null> {
    return this.updatedQuoteData$.asObservable();
  }

  public getQuoteDataValue(): QuotePageData | null {
    return this.quoteData$.getValue();
  }

  public setQuoteData(data: QuotePageData | null): void {
    this.quoteData$.next(data);
  }
  public setUpdatedQuoteData(data: QuotePageData | null): void {
    this.updatedQuoteData$.next(data);
  }

  public getIsMakeContract(): boolean {
    return this.isMakeContract$.getValue();
  }

  public getIsMakePrototype(): boolean {
    return this.isMakePrototype$.getValue();
  }

  public getCurrencyLabel(): string | null {
    return this.currencyLabel$.getValue();
  }

  public isCreateFromExisting(): boolean {
    return this.createFromExisting$.getValue();
  }

  public getParentRelations(): RelationModel[] | null{
    return this.parentRelations$.getValue();
  }

  public clearQuoteData(): void {
    this.quoteData$.next(null);
    this.updatedQuoteData$.next(null);
  }

  public cleanupData(): void {
    this.displaySettings$.next(true);
    this.creationMode$.next(null);
    this.countryId$.next(null);
    this.territoriesIds$.next(null);
    this.account$.next(null);
    this.currencyLabel$.next(null);
    this.createFromExisting$.next(false);
    this.parentRelations$.next(null);
    this.navigateToPak = false;
    this.accountIdToNavigate = null;
  }
}
